// src/pages/SettingsModal.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import SectionsPicker from '../components/SectionsPicker';
import ModuleSections from '../components/ModuleSections';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import { X, MessageCircle, CreditCard, Shield, ShieldCheck, ShieldOff, Copy, Check , FileText, Download, Eye} from 'lucide-react';
import { authApi } from '../api/auth';

interface Ticket {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  subject: string;
  message: string;
  status: 'open' | 'closed';
  replies: Array<{ from: string; message: string; timestamp: string }>;
  createdAt: string;
  updatedAt: string;
  unreadAdminReplies?: boolean;
}

interface Props {
  onClose: () => void;
}

const SettingsModal: React.FC<Props> = ({ onClose }) => {
  const modalOpenedAt = useRef<number>(Date.now());

  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'account' | 'security' | 'support' | 'roles' | 'sections'>('account');
  const [sectionsUserId, setSectionsUserId] = useState<string>('');

  // ─── 2FA ───
  const [twoFAStatus, setTwoFAStatus] = useState<{ enabled: boolean; configuredAt: string | null; backupCodesCount: number } | null>(null);
  const [twoFASetup, setTwoFASetup] = useState<{ secret: string; qrCode: string } | null>(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFABackupCodes, setTwoFABackupCodes] = useState<string[]>([]);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFAStep, setTwoFAStep] = useState<'status' | 'setup' | 'backup' | 'disable'>('status');
  const [twoFADisablePwd, setTwoFADisablePwd] = useState('');
  const [twoFADisableCode, setTwoFADisableCode] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // ─── 2FA: загрузка статуса ───
  const load2FAStatus = useCallback(async () => {
    try {
      const data = await authApi.get2FAStatus();
      setTwoFAStatus(data);
    } catch (e) {
      console.error('Ошибка загрузки 2FA статуса:', e);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'security') {
      load2FAStatus();
    }
  }, [activeTab, load2FAStatus]);

  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const data = await authApi.setup2FA();
      setTwoFASetup({ secret: data.secret, qrCode: data.qrCode });
      setTwoFAStep('setup');
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Не удалось начать настройку 2FA');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!/^\d{6}$/.test(twoFACode.trim())) {
      alert('Введите 6-значный код');
      return;
    }
    setTwoFALoading(true);
    try {
      const data = await authApi.enable2FA(twoFACode.trim());
      setTwoFABackupCodes(data.backupCodes || []);
      setTwoFAStep('backup');
      setTwoFACode('');
      await load2FAStatus();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Неверный код');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!twoFADisablePwd.trim() || !twoFADisableCode.trim()) {
      alert('Введите пароль и код');
      return;
    }
    setTwoFALoading(true);
    try {
      await authApi.disable2FA(twoFADisablePwd, twoFADisableCode.trim());
      setTwoFAStep('status');
      setTwoFADisablePwd('');
      setTwoFADisableCode('');
      await load2FAStatus();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Не удалось отключить 2FA');
    } finally {
      setTwoFALoading(false);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const cancelSetup = () => {
    setTwoFASetup(null);
    setTwoFACode('');
    setTwoFAStep('status');
  };

  const finishBackup = () => {
    setTwoFABackupCodes([]);
    setTwoFAStep('status');
  };

  // Смена пароля
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [rolesUsers, setRolesUsers] = useState<any[]>([]);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [roleModal, setRoleModal] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' }>({ isOpen: false, message: '', type: 'success' });
  const [photoMessage, setPhotoMessage] = useState('');

  // Поддержка
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [replyText, setReplyText] = useState('');
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketMessage, setNewTicketMessage] = useState('');
  const [creating, setCreating] = useState(false);
  const [lastSeenTimestamps, setLastSeenTimestamps] = useState<Record<string, string>>({});

  // Кастомное уведомление
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  } | null>(null);

  const showAlert = (title: string, message: string) => {
    setAlertState({ isOpen: true, title, message });
  };

  const closeAlert = () => setAlertState(null);

  const fetchMyTickets = useCallback(async () => {
    if (!user) return;
    setLoadingTickets(true);
    try {
      const res = await api.get('/tickets/my');
      const ticketsData = res.data.map((t: Ticket) => {
        const lastAdminReply = t.replies.filter(r => r.from === 'admin').sort((a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        )[0];
        const lastSeen = lastSeenTimestamps[t.id] || localStorage.getItem(`ticket_${t.id}_lastSeen`) || t.createdAt;
        const unread = lastAdminReply && new Date(lastAdminReply.timestamp) > new Date(lastSeen);
        return { ...t, unreadAdminReplies: unread };
      });
      setTickets(ticketsData);
    } catch (err) {
      console.error('Ошибка загрузки тикетов', err);
      showAlert('Ошибка', 'Не удалось загрузить обращения');
    } finally {
      setLoadingTickets(false);
    }
  }, [user?.id, lastSeenTimestamps]);

  useEffect(() => {
    if (activeTab === 'support') {
      fetchMyTickets();
    }
  }, [activeTab, fetchMyTickets]);

  useEffect(() => {
    if (activeTab !== 'support') return;
    const interval = setInterval(() => {
      fetchMyTickets();
    }, 15000);
    return () => clearInterval(interval);
  }, [activeTab, fetchMyTickets]);

  // ★ Только показ уведомлений, БЕЗ requestPermission (морозит модалку на mobile)
  useEffect(() => {
    try {
      const unreadCount = tickets.filter(t => t.unreadAdminReplies).length;
      if (unreadCount > 0 && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('Поддержка', { body: `У вас ${unreadCount} новых ответов от администратора` });
      }
    } catch (e) { /* silent */ }
  }, [tickets]);

  // ★ Один запрос при открытии модалки → badge "Помощь (N)" сразу актуален
  useEffect(() => {
    fetchMyTickets();
  }, [fetchMyTickets]);

  const handleSendReply = async () => {
    if (!selectedTicket || !replyText.trim()) return;
    try {
      const res = await api.post(`/tickets/${selectedTicket.id}/reply`, { message: replyText });
      setSelectedTicket({ ...res.data, unreadAdminReplies: false });
      setTickets(tickets.map(t => t.id === res.data.id ? { ...res.data, unreadAdminReplies: false } : t));
      setReplyText('');
    } catch (err) {
      console.error('Ошибка отправки ответа', err);
      showAlert('Ошибка', 'Не удалось отправить ответ');
    }
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTicketSubject.trim() || !newTicketMessage.trim()) {
      showAlert('Заполните поля', 'Тема и сообщение обязательны для заполнения');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/tickets', { subject: newTicketSubject, message: newTicketMessage });
      setTickets([res.data, ...tickets]);
      setNewTicketSubject('');
      setNewTicketMessage('');
      setShowCreateForm(false);
    } catch (err) {
      console.error('Ошибка создания тикета', err);
      showAlert('Ошибка', 'Не удалось создать обращение');
    } finally {
      setCreating(false);
    }
  };

  const handleSelectTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    const now = new Date().toISOString();
    setLastSeenTimestamps(prev => ({ ...prev, [ticket.id]: now }));
    localStorage.setItem(`ticket_${ticket.id}_lastSeen`, now);
    setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, unreadAdminReplies: false } : t));
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setMessage('Новые пароли не совпадают');
      return;
    }
    try {
      await api.put('/auth/change-password', { oldPassword, newPassword });
      setMessage('Пароль успешно изменён');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setMessage(err.response?.data?.error || 'Ошибка при смене пароля');
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const photoData = event.target?.result as string;
      updateUser({ photo: photoData })
        .then(() => setPhotoMessage('Фото обновлено'))
        .catch(() => setPhotoMessage('Ошибка обновления фото'));
    };
    reader.readAsDataURL(file);
  };

  const fetchRoles = async () => {
    try {
      const res = await api.get('/users/roles');
      setRolesUsers(res.data.map((u: any) => ({
        ...u,
        permissions: u.permissions || {
          crmSections: [],
          canViewBi: false,
          canViewInterview: false,
          canViewTasks: false,
          canViewDataDriven: false,
          canViewElearning: false,
          canViewHRM: false,
          canViewHRDocuments: false,
          canManageHRDocuments: false,
          canManageHRTemplates: false,
          canSignHRDocuments: false,
          canManageOnboarding: false,
          canViewHRAudit: false,
          // СРМ Продажи
          canViewSalesCRM: false,
          canManageSalesCRM: false,
          canManageSalesPipelines: false,
          canViewSalesReports: false,
          // СРМ Бухгалтерия
          canViewAccountingCRM: false,
          canManageAccountingCRM: false,
          canViewAccountingReports: false,
          canManageCounterparties: false,
        },
      })));
    } catch (err) {
      console.error('Ошибка загрузки ролей:', err);
    }
  };

  const handlePermissionChange = async (userId: string, permission: string, value: boolean) => {
    setRolesUsers(prev => prev.map(u => {
      if (u.id !== userId) return u;
      const updatedPermissions = { ...u.permissions, [permission]: value };
      return { ...u, permissions: updatedPermissions };
    }));
    try {
      await api.put('/users/permissions', { userId, permissions: { [permission]: value } });
      fetchRoles();
    } catch (err) {
      console.error('Ошибка сохранения прав:', err);
    }
  };
  const handleBulkSectionChange = async (userId: string, paths: string[], value: boolean) => {
    const targetUser = rolesUsers.find((u: any) => u.id === userId);
    const current = new Set<string>(targetUser?.permissions?.allowedSections || []);
    if (value) paths.forEach(p => current.add(p));
    else paths.forEach(p => current.delete(p));
    const arr = Array.from(current);

    setRolesUsers(prev => prev.map(u => u.id === userId
      ? { ...u, permissions: { ...u.permissions, allowedSections: arr } } : u));

    try {
      await api.put(`/permissions/user/${userId}/sections`, { allowedSections: arr });
    } catch (err) {
      console.error('Ошибка bulk сохранения:', err);
    }
  };

  const handleReplaceAllSections = async (userId: string, newSections: string[]) => {
    try {
      setRolesUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, permissions: { ...u.permissions, allowedSections: newSections } } : u));
      await api.put(`/permissions/user/${userId}/sections`, { allowedSections: newSections });
    } catch (err) {
      console.error('Ошибка замены allowedSections:', err);
    }
  };

  const handleAllowedSectionChange = async (userId: string, path: string, checked: boolean) => {
    const targetUser = rolesUsers.find((u: any) => u.id === userId);
    const current = new Set<string>(targetUser?.permissions?.allowedSections || []);
    if (checked) current.add(path); else current.delete(path);
    const arr = Array.from(current);

    setRolesUsers(prev => prev.map(u => u.id === userId
      ? { ...u, permissions: { ...u.permissions, allowedSections: arr } } : u));

    try {
      await api.put(`/permissions/user/${userId}/sections`, { allowedSections: arr });
    } catch (err) {
      console.error('Ошибка сохранения allowedSections:', err);
    }
  };

  const handleCrmSectionChange = async (userId: string, section: string, checked: boolean) => {
    setRolesUsers(prev => prev.map(u => {
      if (u.id !== userId) return u;
      const current = new Set(u.permissions?.crmSections || []);
      if (checked) current.add(section); else current.delete(section);
      return { ...u, permissions: { ...u.permissions, crmSections: Array.from(current) } };
    }));
    try {
      // Отправляем обновлённый массив crmSections
      const targetUser = rolesUsers.find(u => u.id === userId);
      const currentSections = new Set(targetUser?.permissions?.crmSections || []);
      if (checked) currentSections.add(section); else currentSections.delete(section);
      await api.put('/users/permissions', { userId, permissions: { crmSections: Array.from(currentSections) } });
      fetchRoles();
    } catch (err) {
      console.error('Ошибка сохранения CRM секций:', err);
    }
  };

const handleRoleChange = (userId: string, newRole: string) => {
    setRolesUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  const handleSaveRoles = async () => {
    setRolesSaving(true);
    try {
      await api.put('/users/roles', rolesUsers.map(u => ({ id: u.id, role: u.role })));
      setRoleModal({ isOpen: true, message: 'Роли успешно сохранены', type: 'success' });
    } catch (err) {
      console.error('Ошибка сохранения ролей:', err);
      setRoleModal({ isOpen: true, message: 'Не удалось сохранить роли', type: 'error' });
    } finally {
      setRolesSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'roles' && user && (user?.role === 'superadmin' || user?.role === 'admin')) {
      fetchRoles();
    }
  }, [activeTab, user?.id]);

  const crmSectionsList = [
  { key: 'employees', label: 'Сотрудники' },
  { key: 'clients', label: 'Клиенты' },
  { key: 'requisitions', label: 'Заявки на подбор' },
  { key: 'vacancies', label: 'Вакансии' },
  { key: 'candidates', label: 'Кандидаты' },
  { key: 'kanban', label: 'Движение кандидатов' },
  { key: 'talentPool', label: 'Кадровый резерв' },
  { key: 'vacationCalendar', label: 'Календарь отпусков' },
];

const roleDescriptions = {
    sales_manager: {
      title: 'Менеджер по продажам',
      color: '#8b5cf6',
      description: 'Ведение сделок, работа с клиентами, воронка продаж, задачи по продажам.',
      defaultPermissions: {
        canViewSalesCRM: true, canManageSalesCRM: true,
        canViewSalesReports: true,
      },
    },
    accountant: {
      title: 'Бухгалтер',
      color: '#10b981',
      description: 'Счета, платежи, контрагенты, платёжный календарь, отчётность.',
      defaultPermissions: {
        canViewAccountingCRM: true, canManageAccountingCRM: true,
        canViewAccountingReports: true, canManageCounterparties: true,
      },
    },
    kdp: {
      title: 'КДП — кадровое делопроизводство',
      color: '#f59e0b',
      description: 'Кадровые документы, онбординг, приказы, шаблоны по ТК РФ.',
      defaultPermissions: {
        canViewHRDocuments: true, canManageHRDocuments: true,
        canManageHRTemplates: true, canSignHRDocuments: true,
        canManageOnboarding: true,
      },
    },
    superadmin: {
      title: 'Супер Админ',
      color: '#ff4d4f',
      description: 'Полный доступ ко всем компаниям и пользователям. Может назначать администраторов компаний и управлять глобальными настройками.'
    },
    admin: {
      title: 'Администратор',
      color: '#faad14',
      description: 'Полный доступ в рамках своей компании. Управляет пользователями, ролями (HR-менеджер, рекрутер, сотрудник) и контентом.'
    },
    hr_manager: {
      title: 'HR-менеджер',
      color: '#2575fc',
      description: 'Управление сотрудниками, вакансиями, заявками и отчётами. Может утверждать заявки и редактировать вакансии.'
    },
    recruiter: {
      title: 'Рекрутер',
      color: '#52c41a',
      description: 'Работа с кандидатами и вакансиями. Просмотр отчётов и базовая аналитика. Без права управления сотрудниками.'
    },
    employee: {
      title: 'Сотрудник',
      color: '#8c8c8c',
      description: 'Базовый доступ: личный кабинет, просмотр своих данных, обучающие материалы. Без административных функций.'
    }
  };

  const handleGoToPayment = () => {
    navigate('/payment');
    onClose();
  };

  // ★ Защита от мигания
  if (!user) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
            Загрузка...
          </div>
        </div>
      </div>
    );
  }

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();
  const totalUnread = tickets.filter(t => t.unreadAdminReplies).length;

  return (
    <>
      <div
        className="settings-modal-overlay"
        style={styles.overlay}
        onClick={(e) => {
          if (Date.now() - modalOpenedAt.current < 400) return;
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="settings-modal-body" style={styles.modal} onClick={stopPropagation}>
          <button style={styles.closeButton} onClick={onClose}><X size={20} /></button>
          <div style={styles.tabs}>
            <button
              style={{ ...styles.tab, ...(activeTab === 'account' && styles.activeTab) }}
              onClick={() => setActiveTab('account')}
            >
              Настройки аккаунта
            </button>
            <button
              style={{ ...styles.tab, ...(activeTab === 'support' && styles.activeTab) }}
              onClick={() => setActiveTab('support')}
            >
              Помощь
              {totalUnread > 0 && <span style={styles.unreadBadge}>{totalUnread}</span>}
            </button>
            <button
              style={{ ...styles.tab, ...(activeTab === 'security' && styles.activeTab) }}
              onClick={() => setActiveTab('security')}
            >
              🔐 Безопасность
            </button>
            {(user?.role === 'superadmin' || user?.role === 'admin') && (
              <>
                <button
                  style={{ ...styles.tab, ...(activeTab === 'roles' && styles.activeTab) }}
                  onClick={() => setActiveTab('roles')}
                >
                  Роли
                </button>
              </>
            )}
          </div>

          {activeTab === 'account' && (
            <div style={styles.content}>
              <h2 style={styles.title}>Настройки аккаунта</h2>
              {/* ═══ БЛОК СКАЧИВАНИЯ ИНСТРУКЦИИ ═══ */}
              <div style={{
                marginTop: '24px',
                marginBottom: '32px',
                padding: '24px',
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ backgroundColor: '#fee2e2', padding: '12px', borderRadius: '12px' }}>
                    <FileText size={28} color="#ef4444" />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                      Руководство пользователя HR LAMA
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>
                      Полное руководство по всем модулям системы (PDF)
                    </p>
                  </div>
                </div>
                <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#475569', lineHeight: 1.5 }}>
                  В документе собраны подробные инструкции по работе с корпоративным порталом, HRM, КДП, бухгалтерией, CRM и другими разделами. Доступно для пользователей и администраторов.
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <a 
                    href="/docs/HR_LAMA_Guide.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      display: 'inline-flex', alignItems: 'center', gap: '8px', 
                      padding: '12px 24px', backgroundColor: '#2575fc', color: 'white', 
                      borderRadius: '50px', textDecoration: 'none', fontSize: '14px', 
                      fontWeight: 600, transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(37, 117, 252, 0.3)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(37, 117, 252, 0.4)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 117, 252, 0.3)'; }}
                  >
                    <Eye size={18} /> Посмотреть онлайн
                  </a>
                  <a 
                    href="/docs/HR_LAMA_Guide.pdf" 
                    download="HR_LAMA_Guide.pdf"
                    style={{ 
                      display: 'inline-flex', alignItems: 'center', gap: '8px', 
                      padding: '12px 24px', backgroundColor: '#ffffff', color: '#2575fc', 
                      border: '2px solid #2575fc', borderRadius: '50px', textDecoration: 'none', 
                      fontSize: '14px', fontWeight: 600, transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.backgroundColor = '#f0f5ff'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(37, 117, 252, 0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <Download size={18} /> Скачать PDF
                  </a>
                </div>
              </div>
              {/* ═══ КОНЕЦ БЛОКА ═══ */}

              <div style={styles.info}>
                <p><strong>Имя:</strong> {user.name}</p>
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Компания:</strong> {user.company || user.companyName || '—'}</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span><strong>Статус доступа:</strong> {user.paid ? '✅ Оплачен' : '❌ Не оплачен'}</span>
                  {!user.paid && (
                    <button onClick={handleGoToPayment} style={styles.payButton}>
                      <CreditCard size={16} style={{ marginRight: 6 }} />
                      Оплатить подписку
                    </button>
                  )}
                </div>
              </div>

              <h3 style={styles.sectionTitle}>Аватар</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24 }}>
                {user.photo ? (
                  <img src={user.photo} alt="Аватар" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 28 }}>👤</div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.6)', padding: '10px 18px', borderRadius: 40, border: '1px solid rgba(255,255,255,0.5)' }}>
                    {user.photo ? 'Изменить фото' : 'Загрузить фото'}
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                  </label>
                  {photoMessage && <span style={{ fontSize: 13, color: '#28a745' }}>{photoMessage}</span>}
                </div>
              </div>

              <h3 style={styles.sectionTitle}>Сменить пароль</h3>
              <form onSubmit={handlePasswordChange} style={styles.form}>
                <input
                  type="password"
                  placeholder="Старый пароль"
                  value={oldPassword}
                  onChange={e => setOldPassword(e.target.value)}
                  required
                  style={styles.input}
                />
                <input
                  type="password"
                  placeholder="Новый пароль"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  style={styles.input}
                />
                <input
                  type="password"
                  placeholder="Подтвердите пароль"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  style={styles.input}
                />
                <button type="submit" style={styles.button}>Сменить пароль</button>
                {message && <p style={styles.message}>{message}</p>}
              </form>
            </div>
          )}

          {activeTab === 'security' && (
            <div style={styles.content}>
              <h2 style={styles.title}>🔐 Безопасность</h2>

              {/* ─── ЗАГРУЗКА ─── */}
              {!twoFAStatus && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
                  Загрузка...
                </div>
              )}

              {/* ─── СТАТУС + ВКЛЮЧЕНИЕ ─── */}
              {twoFAStatus && twoFAStep === 'status' && (
                <div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '16px', borderRadius: '12px', marginBottom: '20px',
                    background: twoFAStatus.enabled
                      ? 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(5,150,105,0.05))'
                      : 'linear-gradient(135deg, rgba(239,68,68,0.08), rgba(220,38,38,0.04))',
                    border: `1px solid ${twoFAStatus.enabled ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.2)'}`,
                  }}>
                    {twoFAStatus.enabled ? (
                      <ShieldCheck size={32} color="#10b981" />
                    ) : (
                      <ShieldOff size={32} color="#ef4444" />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                        Двухфакторная аутентификация {twoFAStatus.enabled ? 'включена' : 'отключена'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        {twoFAStatus.enabled
                          ? `Включена ${twoFAStatus.configuredAt ? new Date(twoFAStatus.configuredAt).toLocaleDateString('ru-RU') : ''} · Backup-кодов: ${twoFAStatus.backupCodesCount}`
                          : 'Настоятельно рекомендуется для всех администраторов (требование РКН)'}
                      </div>
                    </div>
                  </div>

                  {!twoFAStatus.enabled && (
                    <button
                      onClick={handleSetup2FA}
                      disabled={twoFALoading}
                      style={{
                        width: '100%', padding: '12px', borderRadius: '12px',
                        background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
                        color: '#fff', border: 'none', cursor: 'pointer',
                        fontSize: '14px', fontWeight: '600',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      }}
                    >
                      <Shield size={18} />
                      {twoFALoading ? 'Подготовка...' : 'Включить 2FA'}
                    </button>
                  )}

                  {twoFAStatus.enabled && (
                    <button
                      onClick={() => setTwoFAStep('disable')}
                      style={{
                        width: '100%', padding: '12px', borderRadius: '12px',
                        background: '#fff', color: '#ef4444',
                        border: '1px solid rgba(239,68,68,0.3)', cursor: 'pointer',
                        fontSize: '14px', fontWeight: '600',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      }}
                    >
                      <ShieldOff size={18} />
                      Отключить 2FA
                    </button>
                  )}
                </div>
              )}

              {/* ─── НАСТРОЙКА: QR + подтверждение ─── */}
              {twoFAStep === 'setup' && twoFASetup && (
                <div>
                  <p style={{ fontSize: '14px', color: '#334155', marginBottom: '16px', lineHeight: '1.5' }}>
                    <strong>Шаг 1.</strong> Откройте приложение-аутентификатор (Google Authenticator, Яндекс.Ключ, Authy) и отсканируйте QR-код:
                  </p>

                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <img
                      src={twoFASetup.qrCode}
                      alt="QR-код для 2FA"
                      style={{
                        width: '220px', height: '220px', padding: '8px',
                        background: '#fff', borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                      }}
                    />
                  </div>

                  <details style={{ marginBottom: '16px', fontSize: '13px' }}>
                    <summary style={{ cursor: 'pointer', color: '#6a11cb', fontWeight: '600' }}>
                      Ввести код вручную
                    </summary>
                    <div style={{
                      marginTop: '8px', padding: '12px', background: '#f8fafc',
                      borderRadius: '8px', border: '1px solid #e2e8f0',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
                    }}>
                      <code style={{ fontSize: '12px', wordBreak: 'break-all', color: '#0f172a' }}>
                        {twoFASetup.secret}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(twoFASetup.secret, 'secret')}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6a11cb' }}
                      >
                        {copiedField === 'secret' ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </details>

                  <p style={{ fontSize: '14px', color: '#334155', marginBottom: '8px', lineHeight: '1.5' }}>
                    <strong>Шаг 2.</strong> Введите 6-значный код из приложения:
                  </p>

                  <input
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    autoFocus
                    inputMode="numeric"
                    style={{
                      width: '100%', padding: '14px', borderRadius: '12px',
                      border: '1px solid #cbd5e1', fontSize: '22px',
                      textAlign: 'center', letterSpacing: '8px', fontWeight: '600',
                      boxSizing: 'border-box', marginBottom: '12px',
                    }}
                  />

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={cancelSetup}
                      style={{
                        flex: 1, padding: '12px', borderRadius: '12px',
                        background: '#fff', color: '#64748b',
                        border: '1px solid #cbd5e1', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      onClick={handleEnable2FA}
                      disabled={twoFALoading || twoFACode.length !== 6}
                      style={{
                        flex: 2, padding: '12px', borderRadius: '12px',
                        background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
                        color: '#fff', border: 'none',
                        cursor: twoFACode.length === 6 ? 'pointer' : 'not-allowed',
                        opacity: twoFACode.length === 6 ? 1 : 0.5,
                        fontSize: '14px', fontWeight: '600',
                      }}
                    >
                      {twoFALoading ? 'Проверка...' : 'Включить 2FA'}
                    </button>
                  </div>
                </div>
              )}

              {/* ─── BACKUP-КОДЫ ─── */}
              {twoFAStep === 'backup' && twoFABackupCodes.length > 0 && (
                <div>
                  <div style={{
                    background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
                    borderRadius: '12px', padding: '12px', marginBottom: '16px',
                    fontSize: '13px', color: '#92400e',
                  }}>
                    ⚠️ <strong>ВАЖНО:</strong> Сохраните эти коды в надёжном месте. Каждый код можно использовать ОДИН раз. Если потеряете доступ к приложению — сможете войти с их помощью.
                  </div>

                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px',
                    padding: '12px', background: '#f8fafc', borderRadius: '12px',
                    border: '1px solid #e2e8f0', marginBottom: '16px',
                  }}>
                    {twoFABackupCodes.map((c, i) => (
                      <code key={i} style={{
                        padding: '8px 12px', background: '#fff', borderRadius: '6px',
                        border: '1px solid #e2e8f0', fontSize: '14px',
                        fontFamily: 'monospace', textAlign: 'center', fontWeight: '600',
                      }}>
                        {c}
                      </code>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => copyToClipboard(twoFABackupCodes.join('\n'), 'all')}
                    style={{
                      width: '100%', padding: '10px', borderRadius: '10px',
                      background: '#fff', color: '#6a11cb',
                      border: '1px solid rgba(106,17,203,0.3)', cursor: 'pointer',
                      fontSize: '13px', fontWeight: '600', marginBottom: '12px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    }}
                  >
                    {copiedField === 'all' ? <><Check size={14} /> Скопировано</> : <><Copy size={14} /> Скопировать все</>}
                  </button>

                  <button
                    type="button"
                    onClick={finishBackup}
                    style={{
                      width: '100%', padding: '12px', borderRadius: '12px',
                      background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
                      color: '#fff', border: 'none', cursor: 'pointer',
                      fontSize: '14px', fontWeight: '600',
                    }}
                  >
                    Я сохранил коды →
                  </button>
                </div>
              )}

              {/* ─── ОТКЛЮЧЕНИЕ ─── */}
              {twoFAStep === 'disable' && (
                <div>
                  <p style={{ fontSize: '14px', color: '#334155', marginBottom: '16px', lineHeight: '1.5' }}>
                    Для отключения 2FA подтвердите пароль и введите текущий код из приложения:
                  </p>

                  <input
                    type="password"
                    value={twoFADisablePwd}
                    onChange={(e) => setTwoFADisablePwd(e.target.value)}
                    placeholder="Пароль"
                    style={{
                      width: '100%', padding: '12px', borderRadius: '10px',
                      border: '1px solid #cbd5e1', fontSize: '14px',
                      boxSizing: 'border-box', marginBottom: '10px',
                    }}
                  />

                  <input
                    type="text"
                    value={twoFADisableCode}
                    onChange={(e) => setTwoFADisableCode(e.target.value.replace(/[^0-9A-Fa-f-]/g, '').slice(0, 9))}
                    placeholder="123456 или XXXX-XXXX"
                    inputMode="numeric"
                    style={{
                      width: '100%', padding: '12px', borderRadius: '10px',
                      border: '1px solid #cbd5e1', fontSize: '14px',
                      boxSizing: 'border-box', marginBottom: '12px',
                      textAlign: 'center', letterSpacing: '3px',
                    }}
                  />

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => { setTwoFAStep('status'); setTwoFADisablePwd(''); setTwoFADisableCode(''); }}
                      style={{
                        flex: 1, padding: '12px', borderRadius: '12px',
                        background: '#fff', color: '#64748b',
                        border: '1px solid #cbd5e1', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      onClick={handleDisable2FA}
                      disabled={twoFALoading || !twoFADisablePwd || !twoFADisableCode}
                      style={{
                        flex: 1, padding: '12px', borderRadius: '12px',
                        background: '#ef4444', color: '#fff', border: 'none',
                        cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                        opacity: twoFADisablePwd && twoFADisableCode ? 1 : 0.5,
                      }}
                    >
                      {twoFALoading ? 'Отключение...' : 'Отключить'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'sections' && (
            <div style={styles.content}>
              <h2 style={styles.title}>🔧 Доступ к разделам</h2>
              <p style={{ color: '#666', marginBottom: 20 }}>
                Выберите пользователя и отметьте, к каким разделам CRM, КДП и Бухгалтерии он имеет доступ.
                Пустой список = доступны все разделы модуля (если включён).
              </p>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                  Пользователь
                </label>
                <select
                  value={sectionsUserId}
                  onChange={(e) => setSectionsUserId(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10,
                    border: '1px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit',
                    background: '#fff', cursor: 'pointer',
                  }}
                >
                  <option value="">— выберите пользователя —</option>
                  {rolesUsers.map((u: any) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>
              </div>

              {sectionsUserId ? (
                <SectionsPicker userId={sectionsUserId} />
              ) : (
                <div style={{
                  padding: 40, textAlign: 'center', color: '#94a3b8',
                  background: 'rgba(0,0,0,0.02)', borderRadius: 12,
                }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>👆</div>
                  <div>Выберите пользователя выше</div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'roles' && (
            <div style={styles.content}>
              <h2 style={styles.title}>Управление ролями</h2>
              <p style={{ color: '#666', marginBottom: 20 }}>
                {user?.role === 'superadmin' ? 'Назначайте роли пользователям. Супер Админ может управлять всеми компаниями.' : 'Назначайте роли HR-менеджера, рекрутера и сотрудника в рамках вашей компании.'}
              </p>

              {/* Карточки ролей с описаниями */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
                {(Object.keys(roleDescriptions) as (keyof typeof roleDescriptions)[]).map((key) => { const role = roleDescriptions[key];
                  // Скрыть роли, которые admin не может назначать
                  if (user?.role === 'admin' && ['superadmin', 'admin'].includes(key)) return null;
                  return (
                    <div key={key} style={{ ...styles.roleCard, borderColor: role.color }}>
                      <div style={{ ...styles.roleBadge, background: role.color }}>
                        {role.title}
                      </div>
                      <p style={styles.roleDescription}>{role.description}</p>
                    </div>
                  );
                })}
              </div>

              {/* Таблица пользователей */}
              <div style={{ background: 'rgba(255,255,255,0.6)', borderRadius: 16, padding: 16 }}>
                
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                {rolesUsers.map(u => {
                  const selectedRole = u.role || 'employee';
                  const roleInfo = roleDescriptions[selectedRole as keyof typeof roleDescriptions] || roleDescriptions.employee;
                  const perms = u.permissions || {};
                  return (
                    <div key={u.id} style={{ background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', borderRadius: 16, padding: 16, border: '1px solid rgba(255,255,255,0.9)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 15, color: '#1a1a2e' }}>{u.name}</div>
                          <div style={{ fontSize: 13, color: '#666' }}>{u.email}</div>
                          <div style={{ fontSize: 13, color: '#666' }}>{u.companyName || '—'}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 13, color: roleInfo.color }}>{roleInfo.title}</span>
                          <select
                            value={u.role}
                            onChange={e => handleRoleChange(u.id, e.target.value)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 8,
                              border: `1px solid ${roleInfo.color}`,
                              background: 'white',
                              color: '#333',
                              fontSize: 13,
                              cursor: 'pointer',
                              boxShadow: `0 0 8px ${roleInfo.color}44`,
                              outline: 'none',
                            }}
                          >
                            {(user?.role === 'superadmin' ? Object.keys(roleDescriptions) : ['hr_manager', 'recruiter', 'employee', 'sales_manager', 'accountant', 'kdp']).map(roleKey => {
                              const def = roleDescriptions[roleKey as keyof typeof roleDescriptions];
                              return <option key={roleKey} value={roleKey}>{def.title}</option>;
                            })}
                          </select>
                        </div>
                      </div>
                      {u.role !== 'employee' && (
                        <ModuleSections
                          permissions={perms}
                          allowedSections={perms.allowedSections || []}
                          onPermissionChange={(key, value) => handlePermissionChange(u.id, key, value)}
                          onSectionChange={(path, value) => handleAllowedSectionChange(u.id, path, value)}
                          onBulkChange={(paths, value) => handleBulkSectionChange(u.id, paths, value)}
                          onReplaceAll={(paths) => handleReplaceAllSections(u.id, paths)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              </div>

              <button onClick={handleSaveRoles} disabled={rolesSaving} style={{ ...styles.button, marginTop: 20 }}>
                {rolesSaving ? 'Сохранение...' : 'Сохранить роли'}
              </button>
            </div>
          )}

          {activeTab === 'support' && (
            <div style={styles.content}>
              <div style={styles.supportHeader}>
                <h2 style={styles.title}>Помощь и поддержка</h2>
                {!showCreateForm && (
                  <button onClick={() => setShowCreateForm(true)} style={styles.supportButton}>
                    + Создать обращение
                  </button>
                )}
              </div>

              {showCreateForm && (
                <div style={styles.createForm}>
                  <h3 style={styles.sectionTitle}>Новое обращение</h3>
                  <form onSubmit={handleCreateTicket} style={styles.form}>
                    <input
                      type="text"
                      placeholder="Тема"
                      value={newTicketSubject}
                      onChange={e => setNewTicketSubject(e.target.value)}
                      required
                      style={styles.input}
                    />
                    <textarea
                      placeholder="Сообщение"
                      rows={4}
                      value={newTicketMessage}
                      onChange={e => setNewTicketMessage(e.target.value)}
                      required
                      style={styles.input}
                    />
                    <div style={styles.formButtons}>
                      <button type="button" onClick={() => setShowCreateForm(false)} style={styles.cancelButton}>
                        Отмена
                      </button>
                      <button type="submit" style={styles.button} disabled={creating}>
                        {creating ? 'Отправка...' : 'Отправить'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {!selectedTicket ? (
                <div style={styles.ticketList}>
                  <h3 style={styles.sectionTitle}>Мои обращения</h3>
                  {loadingTickets ? (
                    <p>Загрузка...</p>
                  ) : tickets.length === 0 ? (
                    <p>У вас пока нет обращений.</p>
                  ) : (
                    tickets.map(t => (
                      <div
                        key={t.id}
                        style={styles.ticketItem}
                        onClick={() => handleSelectTicket(t)}
                      >
                        <div style={styles.ticketHeader}>
                          <span style={styles.ticketSubject}>{t.subject}</span>
                          <div style={styles.ticketBadges}>
                            {t.unreadAdminReplies && <MessageCircle size={14} color="#ff4d4f" style={{ marginRight: 4 }} />}
                            <span style={{ ...styles.ticketStatus, color: t.status === 'open' ? '#ff4d4f' : '#52c41a' }}>
                              {t.status === 'open' ? 'Открыт' : 'Закрыт'}
                            </span>
                          </div>
                        </div>
                        <div style={styles.ticketDate}>{new Date(t.createdAt).toLocaleString()}</div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div>
                  <button style={styles.backButton} onClick={() => setSelectedTicket(null)}>
                    ← Назад к списку
                  </button>
                  <h3 style={styles.title}>Тема: {selectedTicket.subject}</h3>
                  <p style={styles.ticketMeta}>
                    Статус: <span style={{ color: selectedTicket.status === 'open' ? '#ff4d4f' : '#52c41a' }}>
                      {selectedTicket.status === 'open' ? 'Открыт' : 'Закрыт'}
                    </span>
                  </p>
                  <p style={styles.ticketMeta}>Создано: {new Date(selectedTicket.createdAt).toLocaleString()}</p>

                  <div style={styles.replies}>
                    <div style={styles.reply}>
                      <strong>Вы:</strong> {selectedTicket.message}
                      <span style={styles.replyDate}>{new Date(selectedTicket.createdAt).toLocaleString()}</span>
                    </div>
                    {selectedTicket.replies.map((r, idx) => (
                      <div key={idx} style={{ ...styles.reply, background: r.from === 'admin' ? 'rgba(37,117,252,0.08)' : 'rgba(0,0,0,0.02)' }}>
                        <strong>{r.from === 'admin' ? 'Администратор' : 'Вы'}:</strong> {r.message}
                        <span style={styles.replyDate}>{new Date(r.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>

                  {selectedTicket.status === 'open' && (
                    <div style={styles.replyForm}>
                      <textarea
                        placeholder="Ваш ответ..."
                        rows={3}
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        style={styles.input}
                      />
                      <button onClick={handleSendReply} style={styles.button}>Отправить ответ</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {roleModal.isOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
          <div style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', borderRadius: '28px', padding: '32px', width: '420px', maxWidth: '90%', boxShadow: '0 25px 50px rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.5)', textAlign: 'center', position: 'relative' }}>
            <img src="/lama.png" alt="Лама" style={{ width: 80, height: 80, objectFit: 'contain', margin: '0 auto 16px' }} />
            <h3 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 700, color: roleModal.type === 'success' ? '#28a745' : '#ff4d4f' }}>
              {roleModal.type === 'success' ? 'Успешно!' : 'Ошибка'}
            </h3>
            <p style={{ fontSize: 16, color: '#333', margin: '0 0 24px', lineHeight: 1.5 }}>{roleModal.message}</p>
            <button
              onClick={() => setRoleModal(prev => ({ ...prev, isOpen: false }))}
              style={{ background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', color: 'white', border: 'none', borderRadius: '40px', padding: '12px 32px', fontSize: 16, fontWeight: 600, cursor: 'pointer', boxShadow: '0 8px 16px rgba(37,117,252,0.2)' }}
            >
              Понятно
            </button>
          </div>
        </div>
      )}

      {alertState && (
        <div style={alertModalStyles.overlay} onClick={closeAlert}>
          <div style={alertModalStyles.modal} onClick={e => e.stopPropagation()}>
            <div style={alertModalStyles.header}>
              <h3 style={alertModalStyles.title}>{alertState.title}</h3>
              <button onClick={closeAlert} style={alertModalStyles.closeButton}>
                <X size={20} />
              </button>
            </div>
            <p style={alertModalStyles.message}>{alertState.message}</p>
            <div style={alertModalStyles.buttons}>
              <button onClick={closeAlert} style={alertModalStyles.confirmButton}>OK</button>
            </div>
          </div>
        </div>
      )}
      {/* ★ Mobile responsive */}
      <style>{`
        @media (max-width: 640px) {
          .settings-modal-overlay { padding: 0 !important; align-items: flex-end !important; }
          .settings-modal-body {
            width: 100% !important;
            max-width: 100% !important;
            height: 95vh !important;
            max-height: 95vh !important;
            border-radius: 20px 20px 0 0 !important;
            overflow-y: auto !important;
          }
        }
      `}</style>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  modal: {
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: '32px',
    padding: '28px',
    maxWidth: '650px',
    width: '90%',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.5)',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: 30,
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#666',
    transition: 'all 0.2s',
    zIndex: 10,
  },
  tabs: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    paddingBottom: '16px',
  },
  tab: {
    padding: '10px 20px',
    background: 'rgba(255,255,255,0.3)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '40px',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
    color: '#4a4a68',
    transition: 'all 0.2s',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  activeTab: {
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    boxShadow: '0 8px 16px rgba(37,117,252,0.2)',
    border: 'none',
  },
  unreadBadge: {
    background: '#ff4d4f',
    color: 'white',
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: 4,
  },
  content: {
    padding: '4px 0',
  },
  title: {
    fontSize: '22px',
    fontWeight: 600,
    marginBottom: '20px',
    color: '#1a1a2e',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '12px',
    color: '#1a1a2e',
  },
  info: {
    marginBottom: '28px',
    padding: '18px',
    background: 'rgba(255,255,255,0.4)',
    backdropFilter: 'blur(8px)',
    borderRadius: '20px',
    border: '1px solid rgba(255,255,255,0.3)',
    fontSize: '14px',
    lineHeight: 1.8,
    color: '#1a1a2e',
  },
  roleCard: {
    background: 'rgba(255,255,255,0.8)',
    borderRadius: 16,
    padding: 16,
    border: '2px solid',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  roleBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: 20,
    color: 'white',
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 10,
  },
  roleDescription: {
    fontSize: 12,
    color: '#555',
    lineHeight: 1.5,
    margin: 0,
  },
  roleButton: {
    padding: '6px 12px',
    borderRadius: 20,
    border: '2px solid transparent',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    transition: 'all 0.2s ease',
    background: 'rgba(255,255,255,0.4)',
    color: '#1a1a2e',
    backdropFilter: 'blur(8px)',
    outline: 'none',
    boxSizing: 'border-box',
  },
  roleSelect: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid rgba(0,0,0,0.15)',
    fontSize: 14,
    background: 'white',
    outline: 'none',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    marginBottom: '20px',
  },
  input: {
    padding: '12px 16px',
    borderRadius: '40px',
    border: '1px solid rgba(255,255,255,0.5)',
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(8px)',
    fontSize: '14px',
    outline: 'none',
    transition: 'border 0.2s',
  },
  button: {
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    padding: '12px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '15px',
    boxShadow: '0 8px 16px rgba(37,117,252,0.2)',
  },
  cancelButton: {
    background: 'rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: '40px',
    padding: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#666',
    fontWeight: 500,
  },
  formButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  message: {
    color: '#28a745',
    fontSize: '14px',
    textAlign: 'center',
    marginTop: 8,
  },
  supportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  supportButton: {
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    boxShadow: '0 8px 16px rgba(37,117,252,0.2)',
  },
  createForm: {
    marginBottom: '24px',
    padding: '20px',
    background: 'rgba(255,255,255,0.4)',
    backdropFilter: 'blur(8px)',
    borderRadius: '24px',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  ticketList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '10px',
  },
  ticketItem: {
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(8px)',
    borderRadius: '20px',
    padding: '16px',
    cursor: 'pointer',
    border: '1px solid rgba(255,255,255,0.3)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  ticketHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    alignItems: 'center',
  },
  ticketBadges: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  ticketSubject: {
    fontWeight: 600,
    color: '#1a1a2e',
  },
  ticketStatus: {
    fontSize: '12px',
    fontWeight: 600,
  },
  ticketDate: {
    fontSize: '12px',
    color: '#888',
  },
  backButton: {
    background: 'transparent',
    border: 'none',
    color: '#2575fc',
    cursor: 'pointer',
    marginBottom: '16px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  ticketMeta: {
    fontSize: '14px',
    margin: '8px 0',
    color: '#4a4a68',
  },
  replies: {
    marginTop: '20px',
    marginBottom: '20px',
    maxHeight: '300px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  reply: {
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(4px)',
    borderRadius: '16px',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  replyDate: {
    fontSize: '10px',
    color: '#999',
    marginLeft: '10px',
    display: 'block',
    marginTop: 4,
  },
  replyForm: {
    marginTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  payButton: {
    background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '30px',
    padding: '8px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: 500,
    boxShadow: '0 6px 12px rgba(40,167,69,0.2)',
  },
};

const alertModalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2100,
  },
  modal: {
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: '28px',
    padding: '28px',
    width: '400px',
    maxWidth: '90%',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
    border: '1px solid rgba(255,255,255,0.5)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1a1a2e',
    margin: 0,
  },
  closeButton: {
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: '40px',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#4a4a68',
    transition: 'all 0.2s',
  },
  message: {
    fontSize: '15px',
    color: '#4a4a68',
    marginBottom: '24px',
    lineHeight: 1.5,
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  confirmButton: {
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    padding: '10px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    boxShadow: '0 8px 16px rgba(37,117,252,0.2)',
  },
};

export default SettingsModal;